import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import {
  getAllArtifacts,
  getArtifact,
  type ArtifactId,
  type OverlayKind,
} from '@/artifacts';
import { useGeoMarkupStore } from '@/store';

/**
 * Workspace-scoped prefix that all overlay routes share.
 * Kept as a constant so every `matchPath` call below uses the exact same
 * segment order.
 */
const MAP_BASE_PATH = '/w/:shortId/map';

/**
 * Result of matching the current pathname against the artifact-registry
 * overlay patterns. `itemId` is absent for `new`-kind overlays.
 */
interface OverlayMatch {
  artifactId: ArtifactId;
  kind: OverlayKind;
  itemId?: string;
}

/**
 * Match the current pathname against every artifact's overlay routes by
 * iterating the registry. Match order per artifact is:
 *   1. `/:itemId/edit`  (most specific — must win over `:itemId`)
 *   2. `/new`           (literal segment — must NOT be mistaken for an itemId)
 *   3. `/:itemId`       (detail)
 *
 * Any itemId equal to the literal `"new"` is rejected defensively so the
 * `/new` form route cannot be misread as a detail route.
 */
function matchOverlay(pathname: string): OverlayMatch | null {
  for (const artifact of getAllArtifacts()) {
    const segment = artifact.singular;
    const artifactId = artifact.id as ArtifactId;

    const editMatch = matchPath(
      { path: `${MAP_BASE_PATH}/${segment}/:itemId/edit`, end: true },
      pathname
    );
    if (editMatch?.params.itemId && editMatch.params.itemId !== 'new') {
      return {
        artifactId,
        kind: 'edit',
        itemId: editMatch.params.itemId,
      };
    }

    const newMatch = matchPath(
      { path: `${MAP_BASE_PATH}/${segment}/new`, end: true },
      pathname
    );
    if (newMatch) {
      return { artifactId, kind: 'new' };
    }

    const detailMatch = matchPath(
      { path: `${MAP_BASE_PATH}/${segment}/:itemId`, end: true },
      pathname
    );
    if (detailMatch?.params.itemId && detailMatch.params.itemId !== 'new') {
      return {
        artifactId,
        kind: 'detail',
        itemId: detailMatch.params.itemId,
      };
    }
  }
  return null;
}

/**
 * Build a stable identity string from an `OverlayMatch`. Used as the
 * effect's dependency so we only react when the URL moves between
 * semantically different overlays — not when React Router produces a new
 * location object with the same pathname.
 */
function overlayIdentity(match: OverlayMatch | null): string {
  if (!match) return '';
  return `${match.artifactId}:${match.kind}:${match.itemId ?? ''}`;
}

/**
 * Bidirectional sync between the URL (modal routes) and the Zustand store
 * flags that drive panel visibility.
 *
 * Direction: **URL → store** (single-directional in this hook).
 * - When the URL matches an overlay, the corresponding artifact slice is
 *   driven directly via `openDetails` / `openForm` / `selectItem`.
 * - When the URL no longer matches an overlay, every artifact's panels are
 *   closed via `closeDetails` / `closeForm`.
 *
 * Cross-artifact exclusion is enforced here at the URL level (closing the
 * "other" artifact's panels before opening the current one). Each slice
 * also defends against this in its own `openDetails`/`openForm`, so this
 * hook's close pass is belt-and-braces.
 *
 * The reverse direction (store → URL) is handled by the click handlers and
 * close handlers of the panels themselves via `navigate()`.
 *
 * Mount this hook once at the `/map` layout level (e.g. inside `MapView`).
 */
export function useSyncOverlayWithUrl(): void {
  const location = useLocation();
  const overlay = matchOverlay(location.pathname);
  const overlayKey = overlayIdentity(overlay);

  useEffect(() => {
    // URL matches no overlay route — close any still-open artifact panels.
    // Reconciles store→URL on every effect run, including a fresh MapView mount:
    // leaving for the Dashboard and returning remounts this hook, so we must
    // close panels on mount too — otherwise a stale `detailsIsOpen` /
    // `formIsOpen` flag re-opens the last artifact over a clean `/map` URL.
    // The loop is a no-op when nothing is open, so first/direct navigation is
    // unaffected.
    if (!overlay) {
      const store = useGeoMarkupStore.getState();
      // closeForm first: tasks auto-reopens details on form close
      for (const artifact of getAllArtifacts()) {
        const slice = artifact.getSlice(store);
        if (slice.formIsOpen) slice.closeForm();
        if (slice.detailsIsOpen) slice.closeDetails();
      }
      return;
    }

    const artifact = getArtifact(overlay.artifactId);
    if (!artifact) return;

    // Cross-artifact exclusion at URL level: close the panels of every
    // OTHER artifact so the new overlay lands on a clean background.
    const store = useGeoMarkupStore.getState();
    for (const otherArtifact of getAllArtifacts()) {
      if (otherArtifact.id === artifact.id) continue;
      const otherSlice = otherArtifact.getSlice(store);
      if (otherSlice.formIsOpen) otherSlice.closeForm();
      if (otherSlice.detailsIsOpen) otherSlice.closeDetails();
    }

    const slice = artifact.getSlice(store);

    if (overlay.kind === 'detail') {
      if (!overlay.itemId) return;
      slice.selectItem(overlay.itemId);
      slice.openDetails();
      return;
    }

    if (overlay.kind === 'edit') {
      if (!overlay.itemId) return;
      slice.selectItem(overlay.itemId);
      slice.openForm('edit', overlay.itemId);
      return;
    }

    if (overlay.kind === 'new') {
      slice.openForm('create');
      return;
    }
    // Effect identity is captured in `overlayKey`; the `overlay` object
    // itself is rebuilt each render but stays stable by value. Slice
    // actions from Zustand are referentially stable and don't belong in
    // the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayKey]);
}
